import { styled } from '@linaria/react'
import React from 'react'
import { FormattedMessage } from 'react-intl'
import { colors } from '../styles/colors'
import { Typography } from '../ui/Typography'
import TonIcon from '../svgr/TonIcon'
import { Rhytm } from '../ui/Rhytm'
import { useReferralUsers } from '../hooks/useBonusData'
import { formatTon } from '../utils/formatTon'
import { ReferralLink } from './ReferralLink'
import { ScrollLoader } from '../system/ScrollLoader'
import { ButtonRegular } from '../ui/Button'
const LIMIT = 50

export const BonusReferralsTab = () => {
  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    refetch,
  } = useReferralUsers(LIMIT)

  if (isLoading) {
    return (
      <CenteredContainer>
        <Typography fontSize={16} color={colors.textSecondary}>
          <FormattedMessage id="bonus.loading" defaultMessage="Загрузка..." />
        </Typography>
      </CenteredContainer>
    )
  }

  if (error) {
    return (
      <CenteredContainer>
        <Typography fontSize={16} color={colors.textSecondary}>
          <FormattedMessage
            id="bonus.referralsError"
            defaultMessage="Ошибка загрузки рефералов"
          />
        </Typography>

        <ButtonRegular
          onClick={() => refetch()}
          backgroundColor={colors.secondary}
          textColor={colors.white}
        >
          <FormattedMessage id="bonus.retry" defaultMessage="Повторить" />
        </ButtonRegular>
      </CenteredContainer>
    )
  }

  const allReferrals = data?.pages.flatMap(page => page.referrals) ?? []
  const isEmpty = !isLoading && allReferrals.length === 0

  if (isEmpty) {
    return (
      <CenteredContainer>
        <ReferralLink />

        <Typography fontSize={16} color={colors.textSecondary}>
          <FormattedMessage
            id="bonus.noReferrals"
            defaultMessage="У вас пока нет рефералов"
          />
        </Typography>
      </CenteredContainer>
    )
  }

  return (
    <div>
      <ReferralLink />

      <ScrollLoader
        loading={isLoading || isFetchingNextPage}
        onShouldLoad={() => {
          if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage()
          }
        }}
      >
        <ReferralsContainer>
          {allReferrals.map((referral, index) => {
            const earnings = formatTon(referral.totalEarnings || '0', 2)
            const earningsNum = parseFloat(earnings)
            const earningsDisplay = earningsNum > 0 ? `+${earnings}` : earnings

            return (
              <ReferralCard key={referral.displayName || index}>
                <ReferralInfo>
                  <Typography fontWeight={700} fontSize={14}>
                    {referral.displayName}
                  </Typography>

                  <Typography
                    fontSize={10}
                    font="roboto"
                    color={colors.textSecondary}
                  >
                    <FormattedMessage
                      id="votes.count"
                      defaultMessage="{count, plural, one {# голос} few {# голоса} many {# голосов} other {# голосов}}"
                      values={{ count: referral.voteCount }}
                    />
                  </Typography>
                </ReferralInfo>

                <EarningsText>
                  <Rhytm
                    gridFlowDirection="column"
                    alignItems="center"
                    gap="2px"
                  >
                    <Typography
                      fontSize={14}
                      fontWeight={600}
                      font="roboto"
                      tag="span"
                    >
                      {earningsDisplay}
                    </Typography>
                    <TonIcon />
                  </Rhytm>
                </EarningsText>
              </ReferralCard>
            )
          })}
        </ReferralsContainer>
      </ScrollLoader>

      {isFetchingNextPage && (
        <LoadingContainer>
          <Typography fontSize={14} color={colors.textSecondary}>
            <FormattedMessage id="bonus.loading" defaultMessage="Загрузка..." />
          </Typography>
        </LoadingContainer>
      )}
    </div>
  )
}

const CenteredContainer = styled.div`
  display: grid;
  gap: 16px;
  width: 100%;
  padding: 24px 0;
  text-align: center;
`

const ReferralsContainer = styled.div`
  display: grid;
  gap: 4px;
`

const ReferralCard = styled.div`
  border: 1px solid ${colors.borderPrimary};
  border-radius: 24px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${colors.surfacePrimary};
`

const ReferralInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
`

const EarningsText = styled.div`
  display: flex;
  align-items: center;
`

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  margin-top: 8px;
`
